import React from 'react';
import { DEFAULT_PRODUCTS } from './productRegistry';
import type { PodusProduct } from './productRegistry';

/** Right-arrow icon for product cards */
function ArrowRight() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M5 12h14M12 5l7 7-7 7"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

/** Skeleton preview card for the right panel */
function SkeletonCard({ title, sections, style }: { title: string; sections: string[]; style?: React.CSSProperties }) {
    return (
        <div
            style={{
                borderRadius: 12,
                border: '1px solid #f3f3f3',
                background: '#fff',
                padding: 20,
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                ...style,
            }}
        >
            <h4
                style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#1a1a1a',
                    margin: '0 0 14px',
                }}
            >
                {title}
            </h4>
            {sections.map((section, i) => (
                <div key={i} style={{ marginTop: i > 0 ? 12 : 0 }}>
                    <p
                        style={{
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            fontSize: 9,
                            fontWeight: 600,
                            color: 'rgba(26,26,26,0.5)',
                            margin: '0 0 6px',
                        }}
                    >
                        {section}
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ height: 3, width: '100%', borderRadius: 9999, background: '#f3f3f3' }} />
                        <div style={{ height: 3, width: '85%', borderRadius: 9999, background: '#f3f3f3' }} />
                    </div>
                    <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                        <div style={{ height: 3, flex: 1, borderRadius: 2, background: '#f3f3f3' }} />
                        <div style={{ height: 3, flex: 1, borderRadius: 2, background: '#f3f3f3' }} />
                        <div style={{ height: 3, flex: 1, borderRadius: 2, background: '#f3f3f3' }} />
                    </div>
                </div>
            ))}
        </div>
    );
}

export interface HeaderServicesMegaMenuProps {
    /** Whether the mega menu is open */
    open: boolean;
    /** Ref attached to the dropdown container for outside-click detection */
    dropdownRef: React.RefObject<HTMLDivElement | null>;
    /** Optional close callback */
    onClose?: () => void;
    /** Product list override */
    products?: PodusProduct[] | undefined;
}

export default function HeaderServicesMegaMenu({
    open,
    dropdownRef: _dropdownRef,
    products = DEFAULT_PRODUCTS,
}: HeaderServicesMegaMenuProps) {
    const containerStyle: React.CSSProperties = {
        maxWidth: 1400,
        margin: '0 auto',
        padding: '0 20px',
    };

    const panelStyle: React.CSSProperties = {
        display: 'flex',
        borderRadius: 16,
        border: '1px solid rgba(229,229,229,0.6)',
        boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        transformOrigin: 'top center',
        transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: open ? 1 : 0,
        transform: open ? 'translateY(0) scaleY(1)' : 'translateY(-8px) scaleY(0.95)',
        pointerEvents: open ? 'auto' : 'none',
    };

    const productCardBase: React.CSSProperties = {
        display: 'block',
        borderRadius: 16,
        padding: '16px 20px',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'all 200ms ease',
    };

    return (
        <div style={containerStyle}>
            <div style={panelStyle}>
                {/* Left — Products Grid */}
                <div
                    style={{
                        flex: 1,
                        background: '#fff',
                        padding: '28px 40px',
                    }}
                >
                    <h3
                        style={{
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            fontSize: 18,
                            fontWeight: 700,
                            color: '#1a1a1a',
                            margin: '0 0 24px',
                        }}
                    >
                        Products
                    </h3>

                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: '16px 20px',
                        }}
                    >
                        {products.map((product, i) => {
                            const ProductIcon = product.Icon;
                            return (
                                <a
                                    key={product.id}
                                    href={product.href}
                                    style={{
                                        ...productCardBase,
                                        background: i === 0 ? '#fff' : 'transparent',
                                        boxShadow: i === 0 ? '0 1px 8px rgba(0,0,0,0.06)' : 'none',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (i !== 0) {
                                            (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.6)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (i !== 0) {
                                            (e.currentTarget as HTMLElement).style.background = 'transparent';
                                        }
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 12,
                                            marginBottom: 12,
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                width: 40,
                                                height: 40,
                                                flexShrink: 0,
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: 12,
                                                background: 'rgba(255, 102, 0, 0.1)',
                                            }}
                                        >
                                            <ProductIcon className={product.iconClassName} />
                                        </div>
                                        <span
                                            style={{
                                                fontFamily: "'Plus Jakarta Sans', sans-serif",
                                                fontSize: 14,
                                                fontWeight: 700,
                                                color: '#1a1a1a',
                                                lineHeight: '1.3',
                                                flex: 1,
                                            }}
                                        >
                                            {product.title}
                                        </span>
                                        <span style={{ color: 'rgba(26,26,26,0.25)', flexShrink: 0 }}>
                                            <ArrowRight />
                                        </span>
                                    </div>
                                    <p
                                        style={{
                                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                                            fontSize: 12,
                                            fontWeight: 400,
                                            color: '#888',
                                            lineHeight: '1.6',
                                            margin: 0,
                                        }}
                                    >
                                        {product.description}
                                    </p>
                                </a>
                            );
                        })}
                    </div>
                </div>

                {/* Right — Preview Panel */}
                <div
                    style={{
                        width: 380,
                        background: '#fff',
                        padding: '28px 32px',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <div style={{ position: 'relative', minHeight: 240, flex: 1 }}>
                        <SkeletonCard
                            title="My Contract"
                            sections={['Summary', 'Experience', 'Skills']}
                            style={{ position: 'absolute', left: 0, top: 16, zIndex: 1, width: 160 }}
                        />
                        <SkeletonCard
                            title="My Contract"
                            sections={['Summary', 'Experience', 'Skills']}
                            style={{ position: 'absolute', left: 95, top: 0, zIndex: 2, width: 160 }}
                        />
                        <SkeletonCard
                            title="My Contract"
                            sections={['Summary', 'Experience']}
                            style={{ position: 'absolute', right: 0, top: 24, zIndex: 3, width: 150 }}
                        />
                    </div>

                    <h4
                        style={{
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            fontSize: 16,
                            fontWeight: 700,
                            color: '#1a1a1a',
                            margin: '20px 0 0',
                        }}
                    >
                        Contract Management
                    </h4>
                    <p
                        style={{
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            fontSize: 12,
                            fontWeight: 400,
                            color: '#888',
                            lineHeight: '1.6',
                            margin: '6px 0 0',
                            maxWidth: 280,
                        }}
                    >
                        Create, review, and manage legal contracts with ease. Streamline your legal workflow.
                    </p>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 16 }}>
                        <span
                            style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: 'rgba(26,26,26,0.15)',
                            }}
                        />
                        <span
                            style={{
                                width: 20,
                                height: 10,
                                borderRadius: 9999,
                                background: '#1a1a1a',
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
